import {
  DeleteOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Descriptions, Drawer, Dropdown, Input, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteContactMutation,
  useGetContactsQuery,
  useUpdateContactStatusMutation,
} from '@/modules/contacts/contactsApi';
import {
  CONTACT_STATUS_OPTIONS,
  type ContactMessage,
  type ContactStatus,
} from '@/types/contact';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import styles from './Contacts.module.scss';

function statusTag(status: ContactStatus) {
  const colors: Record<ContactStatus, string> = {
    new: 'gold',
    read: 'processing',
    replied: 'blue',
    closed: 'default',
  };
  const label = CONTACT_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
  return <Tag color={colors[status]}>{label}</Tag>;
}

export default function Contacts() {
  const notify = useNotify();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContactStatus | 'all'>('all');
  const [selectedContact, setSelectedContact] = useState<ContactMessage | null>(null);

  const hasActiveFilters = search.length > 0 || statusFilter !== 'all';

  const { data: listResult, isLoading, isFetching } = useGetContactsQuery({
    page,
    limit,
    search,
    status: statusFilter,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  useGlobalLoader(isFetching && !!listResult);

  const [updateStatus] = useUpdateContactStatusMutation();
  const [deleteContact] = useDeleteContactMutation();

  const contacts = listResult?.results ?? [];
  const total = listResult?.total ?? 0;

  const applySearch = useCallback(() => {
    setSearch(searchDraft.trim());
    setPage(1);
  }, [searchDraft]);

  const resetFilters = useCallback(() => {
    setSearchDraft('');
    setSearch('');
    setStatusFilter('all');
    setPage(1);
  }, []);

  const handleStatusChange = useCallback(
    async (contact: ContactMessage, status: ContactStatus) => {
      const result = await resolveMutationResult(updateStatus({ id: contact.id, body: { status } }));
      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        setSelectedContact((current) => (current?.id === contact.id ? { ...current, status } : current));
        return;
      }
      notify.error(result.message);
    },
    [notify, updateStatus],
  );

  const handleDelete = useCallback(
    async (contact: ContactMessage) => {
      const confirmed = await confirmDelete(contact.fullName);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteContact(contact.id));
      if (result.ok) {
        notify.success(result.message ?? 'Contact message deleted.');
        setSelectedContact(null);
        return;
      }
      notify.error(result.message);
    },
    [deleteContact, notify],
  );

  const getRowActionItems = useCallback(
    (contact: ContactMessage): MenuProps['items'] => [
      {
        key: 'view',
        label: 'View message',
        icon: <EyeOutlined />,
        onClick: () => setSelectedContact(contact),
      },
      { type: 'divider' },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        icon: <DeleteOutlined />,
        onClick: () => void handleDelete(contact),
      },
    ],
    [handleDelete],
  );

  const columns: ColumnsType<ContactMessage> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'fullName',
        key: 'fullName',
        className: styles.primaryCol,
        render: (value: string, record) => (
          <div>
            <span className={styles.primaryCell}>{value}</span>
            <span className={styles.subCell}>{record.email}</span>
          </div>
        ),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (value?: string) => <span className={styles.metaCell}>{value ?? '—'}</span>,
      },
      {
        title: 'Desk',
        dataIndex: 'desk',
        key: 'desk',
        className: styles.wrapCol,
        render: (value: string) => <span className={styles.metaCell}>{value}</span>,
      },
      {
        title: 'Subject',
        dataIndex: 'subject',
        key: 'subject',
        className: styles.wrapCol,
        render: (value: string) => <span className={styles.primaryCell}>{value}</span>,
      },
      {
        title: 'Submitted',
        dataIndex: 'submittedAt',
        key: 'submittedAt',
        render: (value?: string) => (
          <span className={styles.dateCell}>{value ? formatDateTime(value) : '—'}</span>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        className: styles.statusCol,
        render: (status: ContactStatus) => statusTag(status),
      },
      {
        title: 'Action',
        key: 'actions',
        className: styles.actionCol,
        fixed: 'right',
        render: (_, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [getRowActionItems],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          hasActiveFilters ? (
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          ) : null
        }
      />

      <div className={styles.filtersBar}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search name, email, desk, subject, message..."
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onPressEnter={applySearch}
          className={styles.searchInput}
        />
        <Button type="primary" onClick={applySearch}>
          Search
        </Button>
        <Select
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          options={CONTACT_STATUS_OPTIONS}
          className={styles.statusSelect}
        />
      </div>

      <div className={styles.tableCard}>
        <Table<ContactMessage>
          className={styles.table}
          rowKey="id"
          size="small"
          loading={isLoading && !listResult}
          columns={columns}
          dataSource={contacts}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (totalCount, [start, end]) => `${start}-${end} of ${totalCount}`,
            size: 'small',
            onChange: (nextPage, nextLimit) => {
              setPage(nextPage);
              setLimit(nextLimit);
            },
          }}
          onRow={(record) => ({
            onClick: () => setSelectedContact(record),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <Drawer
        title={selectedContact?.subject ?? 'Contact message'}
        open={!!selectedContact}
        onClose={() => setSelectedContact(null)}
        width={640}
      >
        {selectedContact ? (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Full name">{selectedContact.fullName}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedContact.email}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedContact.phone ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="This message is for">{selectedContact.desk}</Descriptions.Item>
            <Descriptions.Item label="Country">{selectedContact.country || '—'}</Descriptions.Item>
            <Descriptions.Item label="State">{selectedContact.state || '—'}</Descriptions.Item>
            <Descriptions.Item label="City">{selectedContact.city || '—'}</Descriptions.Item>
            <Descriptions.Item label="Subject">{selectedContact.subject}</Descriptions.Item>
            <Descriptions.Item label="Message">
              <p className={styles.message}>{selectedContact.message}</p>
            </Descriptions.Item>
            <Descriptions.Item label="Submitted">
              {selectedContact.submittedAt ? formatDateTime(selectedContact.submittedAt) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Select
                value={selectedContact.status}
                options={CONTACT_STATUS_OPTIONS.filter((option) => option.value !== 'all')}
                onChange={(value) => void handleStatusChange(selectedContact, value)}
                style={{ width: '100%' }}
              />
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>
    </div>
  );
}
